/* ============================================================
 * <product-subscription-popup>
 *   - Auto-opens after a configurable delay on first visit.
 *   - Session-scoped suppression: once closed, stays closed for
 *     the rest of the browser session (sessionStorage).
 *   - Primary CTA: selects the SUBSCRIBE radio on the PDP,
 *     closes the popup, and smoothly scrolls to Add-to-Cart.
 *   - Closes on: X button, overlay click, "No Thanks" link, Esc key.
 * ============================================================ */

if (!customElements.get('product-subscription-popup')) {
  class ProductSubscriptionPopup extends HTMLElement {
    connectedCallback() {
      this.suppressionKey = this.dataset.suppressionKey || 'psp-closed';
      this.showDelay = parseInt(this.dataset.showDelay, 10) || 0;
      this.manualOnly = this.dataset.manualOnly === 'true';

      // Always bind events so the popup can be opened manually
      // (e.g. via the corner launcher) even after suppression.
      this.bindEvents();

      // Auto-open only when not manual-only and not closed this session.
      if (this.manualOnly || this.isSuppressed()) return;

      this.scheduleOpen();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
    }

    isSuppressed() {
      try {
        return window.sessionStorage.getItem(this.suppressionKey) === '1';
      } catch (e) {
        return false;
      }
    }

    markSuppressed() {
      try {
        window.sessionStorage.setItem(this.suppressionKey, '1');
      } catch (e) {
        /* sessionStorage disabled / private mode — silently ignore */
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-psp-close]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      const cta = this.querySelector('[data-psp-cta]');
      if (cta) cta.addEventListener('click', (e) => this.handleCta(e));

      this.handleKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.handleKeydown);
    }

    scheduleOpen() {
      this.openTimer = window.setTimeout(() => this.open(), this.showDelay);
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-subscription-popup-open');
    }

    close() {
      if (this.openTimer) window.clearTimeout(this.openTimer);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-subscription-popup-open');
      this.markSuppressed();
    }

    handleCta(e) {
      e.preventDefault();

      // 1. Ensure the SUBSCRIBE option is selected on the PDP.
      //    Prefer the bundle-purchase-options radio; fall back to any
      //    selling-plans-widget subscription button on legacy PDPs.
      const subscribeRadio = document.querySelector(
        'input[type="radio"][name="bundle_purchase_option"][value="subscribe"]',
      );
      if (subscribeRadio && !subscribeRadio.checked) {
        subscribeRadio.checked = true;
        subscribeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!subscribeRadio) {
        const legacySubBtn = document.querySelector(
          '.purchase-option-btn.purchase-option-btn--subscription',
        );
        if (legacySubBtn && !legacySubBtn.classList.contains('selected')) {
          legacySubBtn.click();
        }
      }

      // 2. Close the popup.
      this.close();

      // 3. Smooth scroll to the Add-to-Cart button so the user can convert.
      window.requestAnimationFrame(() => {
        const atc =
          document.querySelector('[id^="AddToCart-"]') ||
          document.querySelector('button[name="add"]');
        if (atc) {
          atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
          try {
            atc.focus({ preventScroll: true });
          } catch (err) {
            /* older browsers */
          }
        }
      });
    }
  }

  customElements.define('product-subscription-popup', ProductSubscriptionPopup);
}
